import React, { useState, useEffect, useMemo } from "react";
import Header from "../Components/Header";
import Footer from "../Components/Footer";
import OrnamentalDivider from "../Components/OrnamentalDivider";
import { useNavigate } from "react-router-dom";
import "../Styles/Pages.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "../Styles/Book.css";
import { useFirestoreWriter } from "../utils/useFirestoreWriter";
import useFirestoreCollection from "../utils/useFirestoreCollection";

const OPENING_HOUR = 15;
const CLOSING_HOUR = 22;
const LEAD_MIN = 30; // minimum minutes ahead of now
const MAX_NAME = 80;
const MAX_EMAIL = 120;
const MAX_PERSONS = 50;
const ADMINS = ["p_iliakop@yahoo.gr", "mariaperou@hotmail.com"];

// HTML escape so any user-supplied text is safe to embed in email markup.
const esc = (s) =>
  String(s == null ? "" : s).replace(
    /[&<>"']/g,
    (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]
  );

const isDev = process.env.NODE_ENV === "development";

const fmt = (d) =>
  d instanceof Date && !isNaN(d)
    ? d.toLocaleString("en-GB", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";

export default function Book() {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [dateAndTime, setDateAndTime] = useState(null);
  const [persons, setPersons] = useState("");
  const { data: adminData } = useFirestoreCollection("administration");
  const [closingDate, setClosingDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    if (adminData && adminData.length > 0 && adminData[0].closingDate) {
      const cd = adminData[0].closingDate;
      setClosingDate(typeof cd.toDate === "function" ? cd.toDate() : new Date(cd));
    }
  }, [adminData]);

  const { addDocument } = useFirestoreWriter("reservations");

  const { minDate, maxDate, minTime, maxTime } = useMemo(() => {
    const now = new Date();
    const minD = new Date(now);
    minD.setHours(0, 0, 0, 0);
    const maxD = closingDate ? new Date(closingDate) : null;
    const dayStart = new Date(now);
    dayStart.setHours(OPENING_HOUR, 0, 0, 0);
    const dayEnd = new Date(now);
    dayEnd.setHours(CLOSING_HOUR, 0, 0, 0);
    return { minDate: minD, maxDate: maxD, minTime: dayStart, maxTime: dayEnd };
  }, [closingDate]);

  const filterTime = (time) => {
    const t = new Date(time);
    const now = new Date();
    if (t.toDateString() !== now.toDateString()) return true;
    return t.getTime() >= now.getTime() + LEAD_MIN * 60 * 1000;
  };

  const filterDate = (d) => {
    const day = new Date(d);
    day.setHours(CLOSING_HOUR, 0, 0, 0);
    return day.getTime() > new Date().getTime();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    if (!(dateAndTime instanceof Date) || isNaN(dateAndTime)) {
      setErrorMsg("Please pick a valid date and time.");
      return;
    }
    if (dateAndTime.getTime() < Date.now() + LEAD_MIN * 60 * 1000) {
      setErrorMsg("Please pick a time at least " + LEAD_MIN + " minutes from now.");
      return;
    }
    const guests = parseInt(persons, 10);
    if (!guests || guests < 1 || guests > MAX_PERSONS) {
      setErrorMsg("Please enter a valid number of guests (1 – " + MAX_PERSONS + ").");
      return;
    }
    if (fullName.length > MAX_NAME) {
      setErrorMsg("Please shorten your name (max " + MAX_NAME + " characters).");
      return;
    }
    if (email.length > MAX_EMAIL) {
      setErrorMsg("Please use a shorter email address.");
      return;
    }

    setLoading(true);
    const niceDate = fmt(dateAndTime);

    try {
      const resRef = await addDocument({
        Name: fullName,
        Email: email,
        Date: dateAndTime,
        Date_created: new Date(),
        Persons: guests,
        status: "pending",
        adminNote: "",
        to: ADMINS,
        message: {
          subject: "New reservation request from " + esc(fullName),
          html:
            "<p>A new reservation request is waiting in the admin dashboard.</p>" +
            "<ul><li><b>Name:</b> " + esc(fullName) + "</li>" +
            "<li><b>Guests:</b> " + guests + "</li>" +
            "<li><b>Requested for:</b> " + esc(niceDate) + "</li>" +
            "<li><b>Customer email:</b> " + esc(email) + "</li></ul>",
        },
      });
      if (isDev) console.log("[booking] reservation id=" + (resRef && resRef.id));

      const custRef = await addDocument({
        _emailOnly: true,
        to: [email],
        message: {
          subject: "We received your reservation request",
          html:
            "<p>Hi " + esc(fullName) + ",</p>" +
            "<p>Thank you for choosing <b>The Old House</b>. " +
            "We have received your reservation request for <b>" +
            guests + " guest" + (guests > 1 ? "s" : "") +
            "</b> on <b>" + esc(niceDate) + "</b>.</p>" +
            "<p>Your request is currently <b>pending</b>. " +
            "We will email you a confirmation shortly.</p>" +
            "<p>Warm regards,<br/>The Old House team</p>",
        },
      });
      if (isDev) console.log("[booking] customer pending id=" + (custRef && custRef.id));

      setSuccess(true);
      setTimeout(() => navigate("/"), 3500);
    } catch (err) {
      console.error("[booking] submit failed:", err);
      setErrorMsg("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page_content">
      <Header />
      <section className="book_form" aria-labelledby="book-heading">
        <h1 id="book-heading">Reserve a Table</h1>
        <OrnamentalDivider />
        {!success && (
          <p className="book_form__intro">
            Submit your request and we will confirm it by email shortly.
            We hold your table for fifteen minutes after the chosen time.
          </p>
        )}
        {loading && <p className="loading">Sending your reservation...</p>}
        {success ? (
          <div className="success">
            <h3>Thank you!</h3>
            <p>Your request has been received. You will get an email as soon as our team confirms it.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <label className="field">
              <span className="field__label">Full name</span>
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
                placeholder="Your name"
              />
            </label>
            <label className="field">
              <span className="field__label">Email</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@example.com"
              />
            </label>
            <div className="field dateAndTime">
              <span className="field__label">Date and time</span>
              <DatePicker
                selected={dateAndTime}
                onChange={(d) => setDateAndTime(d)}
                showTimeSelect
                dateFormat="d MMMM, yyyy HH:mm"
                timeFormat="HH:mm"
                minDate={minDate}
                maxDate={maxDate || undefined}
                minTime={minTime}
                maxTime={maxTime}
                filterTime={filterTime}
                filterDate={filterDate}
                timeIntervals={30}
                shouldCloseOnSelect={true}
                placeholderText="Pick a date and time"
                isClearable
                autoComplete="off"
                popperPlacement="bottom-start"
                popperProps={{ strategy: "fixed" }}
                po
rtalId="datepicker-portal"
              />
            </div>
            <label className="field">
              <span className="field__label">Number of guests</span>
              <input
                type="number"
                value={persons}
                onChange={(e) => setPersons(e.target.value)}
                required
                min="1"
                placeholder="e.g. 4"
              />
            </label>
            {errorMsg && <p className="book_form__error" role="alert">{errorMsg}</p>}
            <button type="submit" disabled={loading}>
              Send reservation request
            </button>
          </form>
        )}
      </section>
      <Footer />
    </div>
  );
}
